/**
 * Toast — feedback d'action centralisé (L13.15).
 *
 * Remplace le pattern ad-hoc répété dans les pages :
 *   `setSuccess('…')` + `<Alert variant="success">` + `setTimeout(2000)`.
 * Ce pattern poussait le contenu vers le bas (layout shift) et était réimplémenté
 * différemment partout. Le toast s'affiche en surimpression (aucun décalage du
 * contenu), avec un style unique et une auto-fermeture cohérente.
 *
 * RÈGLE D'OR R13 : ce composant ne remplace PAS window.alert/confirm/prompt
 * (ça, c'est <Dialog>/<ConfirmDialog>). Le toast est un retour passif et
 * non bloquant — il n'attend aucune décision de l'utilisateur.
 *
 * Quand utiliser un toast vs une Alert :
 *   - Toast  : retour APRÈS une action (enregistré, supprimé, envoyé, erreur d'action).
 *   - Alert  : message PERSISTANT lié au contenu (erreur de chargement de page,
 *              avertissement de section, info contextuelle).
 *
 * Mise en place : <ToastProvider> englobe l'app (App.tsx). Les pages appellent
 * `const { toast } = useToast();` puis `toast.success('Enregistré')`.
 *
 * Accessibilité :
 *   - conteneur `aria-live="polite"` → les succès/infos sont annoncés sans voler le focus
 *   - les toasts d'erreur portent `role="alert"` (annonce assertive)
 *   - bouton de fermeture avec `aria-label`, touch target ≥ 24px
 *   - `prefers-reduced-motion` : transition d'entrée/sortie neutralisée globalement
 *     (globals.css) — le toast reste fonctionnel, juste sans glissement.
 *
 * Capacitor / mobile : conteneur positionné en haut, sous l'encoche
 * (`env(safe-area-inset-top)`), centré sur mobile et aligné à droite sur ≥ sm.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { clsx } from 'clsx';
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

interface ToastOptions {
  /** Titre optionnel en gras au-dessus du message. */
  title?: string;
  /** Durée d'affichage en ms avant fermeture auto. 0 = ne se ferme pas seul. Défaut 4500. */
  duration?: number;
}

interface ToastItem extends ToastOptions {
  id: number;
  variant: ToastVariant;
  message: string;
  /** Passe à true pendant l'animation de sortie. */
  leaving: boolean;
}

/** Fonction toast : appelable directement ou via ses raccourcis de variante. */
interface ToastApi {
  (variant: ToastVariant, message: string, options?: ToastOptions): number;
  success: (message: string, options?: ToastOptions) => number;
  error: (message: string, options?: ToastOptions) => number;
  warning: (message: string, options?: ToastOptions) => number;
  info: (message: string, options?: ToastOptions) => number;
  /** Ferme un toast par son id (rarement utile — l'auto-fermeture suffit). */
  dismiss: (id: number) => void;
}

interface ToastContextValue {
  toast: ToastApi;
}

// ============================================================================
// Contexte
// ============================================================================

const ToastContext = createContext<ToastContextValue | null>(null);

/** Hook d'accès aux toasts. Doit être appelé sous un <ToastProvider>. */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error(
      'useToast() doit être utilisé à l\'intérieur d\'un <ToastProvider> (voir App.tsx).',
    );
  }
  return ctx;
}

// ============================================================================
// Apparence par variante
// ============================================================================

const VARIANT_META: Record<
  ToastVariant,
  { icon: LucideIcon; ring: string; iconColor: string; bar: string }
> = {
  success: { icon: CheckCircle2,  ring: 'border-success/35', iconColor: 'text-success', bar: 'bg-success' },
  error:   { icon: AlertCircle,   ring: 'border-urgent/40',  iconColor: 'text-urgent',  bar: 'bg-urgent' },
  warning: { icon: AlertTriangle, ring: 'border-warning/40', iconColor: 'text-warning', bar: 'bg-warning' },
  info:    { icon: Info,          ring: 'border-info/35',    iconColor: 'text-info',    bar: 'bg-info' },
};

const DEFAULT_DURATION = 4500;
const MAX_VISIBLE = 4;
const EXIT_MS = 200;

// ============================================================================
// Provider
// ============================================================================

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  // Timers d'auto-fermeture, indexés par id de toast — nettoyés à l'unmount.
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const clearTimer = useCallback((id: number) => {
    const t = timers.current.get(id);
    if (t) {
      clearTimeout(t);
      timers.current.delete(id);
    }
  }, []);

  // Retire définitivement un toast du DOM.
  const remove = useCallback((id: number) => {
    clearTimer(id);
    setToasts((list) => list.filter((t) => t.id !== id));
  }, [clearTimer]);

  // Lance l'animation de sortie puis retire le toast.
  const dismiss = useCallback((id: number) => {
    clearTimer(id);
    setToasts((list) => list.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    window.setTimeout(() => remove(id), EXIT_MS);
  }, [clearTimer, remove]);

  // Empile un nouveau toast.
  const push = useCallback(
    (variant: ToastVariant, message: string, options?: ToastOptions): number => {
      const id = nextId.current++;
      const duration = options?.duration ?? DEFAULT_DURATION;
      const item: ToastItem = {
        id,
        variant,
        message,
        title: options?.title,
        duration,
        leaving: false,
      };

      setToasts((list) => {
        // Limite la pile : au-delà de MAX_VISIBLE, on évince le plus ancien.
        const next = [...list, item];
        if (next.length > MAX_VISIBLE) {
          const evicted = next.shift();
          if (evicted) clearTimer(evicted.id);
        }
        return next;
      });

      if (duration > 0) {
        // `setTimeout` nu (pas `window.setTimeout`) : depuis que les tests web
        // amènent @types/node, `ReturnType<typeof setTimeout>` se résout côté
        // Node quand `window.setTimeout` rend un number — le global, lui, suit
        // le même type que la déclaration de la map, quel que soit l'arbitre.
        const handle = setTimeout(() => dismiss(id), duration);
        timers.current.set(id, handle);
      }
      return id;
    },
    [clearTimer, dismiss],
  );

  // Nettoyage de tous les timers à l'unmount du provider.
  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach((t) => clearTimeout(t));
      map.clear();
    };
  }, []);

  // API stable exposée par le contexte.
  const api = useMemo<ToastApi>(() => {
    const fn = ((variant: ToastVariant, message: string, options?: ToastOptions) =>
      push(variant, message, options)) as ToastApi;
    fn.success = (message, options) => push('success', message, options);
    fn.error = (message, options) => push('error', message, options);
    fn.warning = (message, options) => push('warning', message, options);
    fn.info = (message, options) => push('info', message, options);
    fn.dismiss = dismiss;
    return fn;
  }, [push, dismiss]);

  const value = useMemo(() => ({ toast: api }), [api]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

// ============================================================================
// Viewport — conteneur fixe + pile de toasts
// ============================================================================

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
}) {
  if (toasts.length === 0) return null;

  return (
    <div
      // aria-live polite : annonce les toasts sans voler le focus de l'utilisateur.
      aria-live="polite"
      aria-relevant="additions"
      className={clsx(
        'pointer-events-none fixed inset-x-0 top-0 z-[100] flex flex-col items-center gap-2',
        'px-3 sm:items-end sm:px-4',
      )}
      style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

// ============================================================================
// Carte d'un toast
// ============================================================================

function ToastCard({
  toast,
  onDismiss,
}: {
  toast: ToastItem;
  onDismiss: (id: number) => void;
}) {
  const [entered, setEntered] = useState(false);
  const meta = VARIANT_META[toast.variant];
  const Icon = meta.icon;

  // Déclenche la transition d'entrée à la frame suivant le montage.
  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const shown = entered && !toast.leaving;

  return (
    <div
      role={toast.variant === 'error' ? 'alert' : 'status'}
      className={clsx(
        'pointer-events-auto w-full max-w-sm overflow-hidden',
        'rounded-r-xl border bg-surface/85 backdrop-blur-glass-hi shadow-glass-hi',
        meta.ring,
        'transition-all duration-200 ease-out-expo',
        shown ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0',
      )}
    >
      <div className="flex items-start gap-3 px-3.5 py-3">
        {/* Barre de couleur latérale */}
        <span
          aria-hidden="true"
          className={clsx('mt-0.5 h-8 w-1 shrink-0 rounded-full', meta.bar)}
        />

        <Icon
          className={clsx('mt-0.5 h-4 w-4 shrink-0', meta.iconColor)}
          aria-hidden="true"
        />

        <div className="min-w-0 flex-1">
          {toast.title && (
            <p className="text-sm font-semibold text-ink leading-snug">{toast.title}</p>
          )}
          <p
            className={clsx(
              'text-sm text-ink-muted leading-snug',
              toast.title && 'mt-0.5',
            )}
          >
            {toast.message}
          </p>
        </div>

        <button
          type="button"
          onClick={() => onDismiss(toast.id)}
          aria-label="Fermer la notification"
          className={clsx(
            'shrink-0 -mr-1 -mt-0.5 flex h-6 w-6 items-center justify-center rounded-r-md',
            'text-ink-dim transition-colors hover:bg-surface-hi hover:text-ink',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech',
            'focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
          )}
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
